import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface FeatureSectionProps {
    id: string;
    title: string;
    description: string;
    visual: React.ReactNode;
    benefits: string[];
    isActive?: boolean;
}

const Section = styled.section`
  padding: 40px 0 40px 20px;
  scroll-margin-top: 100px;
  position: relative;
  border-left: 2px solid #e5e7eb;
  margin-left: 10px;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  
  &:last-child {
    border-left: none;
    padding-bottom: 0;
    min-height: auto;
  }

  @media (max-width: 1024px) {
    min-height: auto;
    padding: 32px 0 32px 16px;
  }

  @media (max-width: 480px) {
    padding: 24px 0 24px 12px;
    margin-left: 6px;
  }
`;

const Blip = styled.div<{ isActive?: boolean }>`
  position: absolute;
  left: -9px;
  top: 45px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.isActive ? '#7c3aed' : '#ffffff'};
  border: 2px solid #7c3aed;
  z-index: 10;
  transition: all 0.3s ease;
  transform: ${props => props.isActive ? 'scale(1.25)' : 'scale(1)'};
  box-shadow: ${props => props.isActive ? '0 0 0 4px rgba(124, 58, 237, 0.2)' : 'none'};

  @media (max-width: 480px) {
    width: 14px;
    height: 14px;
    left: -8px;
    top: 30px;
  }
`;

const Container = styled.div<{ isActive?: boolean }>`
  display: block;
  opacity: ${props => props.isActive ? 1 : 0.5};
  transition: opacity 0.3s ease;
`;

const Content = styled.div`
  padding-left: 24px;
  position: relative;

  @media (max-width: 480px) {
    padding-left: 16px;
  }
`;

const VisualWrapper = styled.div`
  /* Hidden by default - MobileVisual in parent handles mobile display */
  display: none;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const Description = styled.p`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.95rem;
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #4b5563;
  font-weight: 500;
  font-size: 0.875rem;
  
  svg {
    color: #7c3aed;
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }
`;

const FeatureSection: React.FC<FeatureSectionProps> = ({ id, title, description, visual, benefits, isActive }) => {
    return (
        <Section id={id}>
            <Blip isActive={isActive} />
            <Container isActive={isActive}>
                <Content>
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                    >
                        <Title>{title}</Title>
                        <Description>{description}</Description>
                        <BenefitList>
                            {benefits.map((benefit, index) => (
                                <BenefitItem key={index}>
                                    <Check />
                                    {benefit}
                                </BenefitItem>
                            ))}
                        </BenefitList>
                    </motion.div>
                </Content>
                {visual && (
                    <VisualWrapper>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            style={{ width: '100%', height: '100%' }}
                        >
                            {visual}
                        </motion.div>
                    </VisualWrapper>
                )}
            </Container>
        </Section>
    );
};

export default FeatureSection;
